import React,{useMemo,useState} from 'react';
import {PRESALE_COMPANIES,PRESALE_STATUS,calculatePresaleReport,reportCsv,reportDateRange} from './presaleReporting.js';
import './presaleReport.css';

const currency=c=>new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(Number(c||0)/100);
const statusChoices=[['all','Todas as situações'],...Object.entries(PRESALE_STATUS)];
const companyChoices=[['all','Duas empresas'],...PRESALE_COMPANIES.map(c=>[c.id,c.name])];
const dateFmt=dt=>new Intl.DateTimeFormat('pt-BR',{dateStyle:'short',timeStyle:'short',timeZone:'America/Sao_Paulo'}).format(new Date(dt));

export default function PresalesReport({presales=[],loading=false,queueError='',onRefresh,onSubmit,submitting=false,completeSource=false}){
 const [org,setOrg]=useState('all'),[status,setStatus]=useState('all');
 const [range,setRange]=useState('all'),[search,setSearch]=useState('');
 const [selected,setSelected]=useState('');
 const [notice,setNotice]=useState('');
 const month=reportDateRange();
 const filters=range==='month'?month:range==='today'?{start:month.end,end:month.end}:{start:'',end:''};
 const report=useMemo(()=>calculatePresaleReport(presales,{org,status,term:search,...filters,completeSource}),[presales,org,status,search,range,completeSource]);
 const active=report.rows.find(r=>r.id===selected)||null;
 function exportReport(){
   const csv=reportCsv(report);
   const url=URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8'}));
   try{
     const a=document.createElement('a');a.href=url;a.download='kaizen-relatorio-pre-vendas-'+month.end+'.csv';
     document.body.appendChild(a);a.click();a.remove();
     setNotice('Arquivo de pré-vendas exportado. Valores não representam faturamento recebido.');
   }finally{setTimeout(()=>URL.revokeObjectURL(url),5000);}
 }
 return <div className="admin presaleReport" aria-label="Relatórios de pré-vendas não fiscais">
   <header className="pageHeader">
     <div><p className="eyebrow">CASA DA LIMPEZA + CONSERTCELL</p>
       <h1>Relatórios de pré-vendas</h1>
       <p>Controle de propostas internas, com valores previstos por empresa e situação. Nenhum valor representa recebimento, venda fiscal ou saldo de caixa.</p></div>
   </header>
   {queueError&&<p role="alert" className="presaleError">{queueError}</p>}
   <div className="presaleReportControls panel">
     <label>Período <select value={range} onChange={e=>setRange(e.target.value)}>
       <option value="all">Todo o histórico disponível</option>
       <option value="month">Mês atual</option><option value="today">Hoje</option>
     </select></label>
     <label>Empresa <select value={org} onChange={e=>setOrg(e.target.value)}>
       {companyChoices.map(([id,name])=><option key={id} value={id}>{name}</option>)}</select></label>
     <label>Situação <select value={status} onChange={e=>setStatus(e.target.value)}>
       {statusChoices.map(([id,name])=><option key={id} value={id}>{name}</option>)}</select></label>
     <label>Buscar número <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Número da pré-venda" /></label>
     <div className="presaleReportActions">
       <button type="button" onClick={()=>onRefresh?.()} disabled={loading}>Atualizar</button>
       <button type="button" onClick={exportReport} disabled={!report.rows.length}>Exportar CSV</button>
       <button type="button" onClick={()=>window.print()} disabled={!report.rows.length}>Imprimir</button>
     </div>
   </div>
   {notice&&<p role="status">{notice}</p>}
   {report.partial&&<p className="presaleError" role="alert">
     Relatório incompleto: não foi possível carregar todo o histórico autorizado.
     Não utilize esses totais para fechamento do período sem conferir o banco.
   </p>}
   <div className="presaleReportTotals">
     <section className="panel"><small>Pré-vendas no filtro</small><strong>{report.count}</strong><small>Registros não fiscais</small></section>
     <section className="panel"><small>Propostas ativas · valor previsto</small><strong>{currency(report.total_cents)}</strong><small>Não recebido</small></section>
     <section className="panel"><small>A confirmar</small><strong>{report.byStatus.pending_confirmation||0}</strong><small>Aguardando conferência</small></section>
     <section className="panel"><small>Rascunhos</small><strong>{report.byStatus.draft||0}</strong><small>Ainda não encaminhados</small></section>
   </div>
   <div className="presaleReportCompanyTotals">
     {report.byCompany.map(c=><section className="panel" key={c.id}>
       <strong>{c.name}</strong><span>{c.count} pré-venda(s) com itens desta empresa</span>
       <b>{currency(c.total_cents)}</b><small>Valor previsto, não faturamento</small>
     </section>)}
   </div>
   <section className="panel">
     <h2>Pré-vendas por período e situação</h2>
     {!report.rows.length?<p>Não existem pré-vendas neste filtro. Isso não significa erro no catálogo.</p>:
     <div className="presaleReportTableWrap"><table className="presaleReportTable">
       <thead><tr><th>Registro</th><th>Data</th><th>Situação</th><th>Empresas</th><th>Previsto</th><th className="presaleReportCommands">Ação</th></tr></thead>
       <tbody>{report.rows.map(p=><tr key={p.id}>
         <td>{p.number||p.id.slice(0,8)}</td>
         <td>{dateFmt(p.created_at)}</td><td>{p.status_label}</td>
         <td>{p.sellers.map(s=>s.company).join(' + ')}</td>
         <td>{currency(p.total_cents)}</td>
         <td className="presaleReportCommands"><button type="button" onClick={()=>setSelected(p.id)}>Conferir</button></td>
       </tr>)}</tbody>
     </table></div>}
   </section>
   {active&&<section className="panel presaleReportDetail" role="region" aria-label="Detalhes da pré-venda">
      <h2>Pré-venda {active.number}</h2><p>Situação: <strong>{active.status_label}</strong></p>
      <p>Criada em {dateFmt(active.created_at)}. Nenhum pagamento ou documento fiscal foi emitido.</p>
      {active.sellers.map(s=><p key={s.organization_id}><strong>{s.company}</strong> — {currency(s.subtotal_cents)}</p>)}
      <strong>Valor previsto: {currency(active.total_cents)}</strong>
      <div className="presaleReportActions">
        {active.status==='draft'&&<button type="button" disabled={submitting||typeof onSubmit!=='function'}
           onClick={()=>onSubmit(active.id)}>Encaminhar à conferência</button>}
        <button type="button" onClick={()=>setSelected('')}>Fechar detalhes</button>
      </div>
      <p className="presaleReportSafety">PRÉ-VENDA — NÃO É DOCUMENTO FISCAL. NÃO COMPROVA PAGAMENTO.</p>
   </section>}
   <p className="presaleReportSafety">Relatório operacional de pré-vendas internas. Recebimento, conciliação, baixa de estoque e emissão fiscal não estão habilitados neste ambiente.</p>
 </div>;
}
